/**
 * Shared presentation helpers for the rep "My leads" screens — status badge
 * mapping and the SLA "Contact by" state (claret when at-risk). Bilingual labels
 * resolve through the host `t`; fallbacks use L() so they never render both
 * languages (the audit-ui I18N-RAW contract).
 */
import type { TFunction } from 'i18next';
import type { BadgeProps } from '@teivor/shared-ui';
import { L } from '@teivor/shared-utils/i18n';

import type { RepTaskStatus } from './types';

export interface StatusBadgeSpec {
  variant: NonNullable<BadgeProps['variant']>;
  key: string;
  fallback: string;
}

export function statusBadge(status: RepTaskStatus): StatusBadgeSpec {
  switch (status) {
    case 'open':
      return { variant: 'info', key: 'com.rep.status.open', fallback: L('Otevřeno', 'Open') };
    case 'worked':
      return { variant: 'success', key: 'com.rep.status.worked', fallback: L('Kontaktováno', 'Worked') };
    case 'escalated_to_am':
      return { variant: 'warning', key: 'com.rep.status.escalated', fallback: L('Předáno AM', 'Escalated to AM') };
    case 'closed':
      return { variant: 'neutral', key: 'com.rep.status.closed', fallback: L('Uzavřeno', 'Closed') };
    default:
      return { variant: 'neutral', key: 'com.rep.status.unknown', fallback: status };
  }
}

export function statusLabel(status: RepTaskStatus, t: TFunction): string {
  const spec = statusBadge(status);
  return t(spec.key, spec.fallback);
}

const AT_RISK_MS = 2 * 60 * 60 * 1000; // within 2h of the first-touch deadline

export type DueState = 'overdue' | 'at_risk' | 'ok';

/** SLA state for a first-touch deadline. Only `open` tasks are time-pressured. */
export function dueState(dueIso: string, status: RepTaskStatus, now: number = Date.now()): DueState {
  if (status !== 'open') return 'ok';
  const due = new Date(dueIso).getTime();
  if (Number.isNaN(due)) return 'ok';
  if (due <= now) return 'overdue';
  if (due - now <= AT_RISK_MS) return 'at_risk';
  return 'ok';
}

export function isAtRisk(dueIso: string, status: RepTaskStatus, now: number = Date.now()): boolean {
  const s = dueState(dueIso, status, now);
  return s === 'overdue' || s === 'at_risk';
}
